import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, HStack, Stack, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Layer,
  Rectangle,
  ResponsiveContainer,
  Sankey,
  Tooltip,
} from 'recharts';
import { customTheme } from 'theme';

import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { actions } from 'containers/Visibility/Vulnerabilities/slice';

import { FilterChain } from './FilterChain';
import {
  activeFilters,
  buildFlowGraph,
  describeFlowTooltip,
  drillPath,
  FLOW_MEASURES,
  FlowChainState,
  FlowLink,
  FlowMeasure,
  FlowNode,
  formatCount,
  formatPercent,
  loadFlowChain,
  saveFlowChain,
  stageDeltas,
} from './utils';

interface Props {
  setShow: (value: string) => void;
}

const colors = customTheme.colors;
// Categorical hues kept clear of the red/orange/yellow severity scale.
const MEASURE_COLORS: Record<FlowMeasure, string> = {
  packages: '#2a78d6',
  cves: '#6250d6',
  hosts: '#1baf7a',
};
const SEVERITY_COLORS: Record<string, string> = {
  critical: colors.critical,
  high: colors.high,
  medium: colors.medium,
  low: colors.low,
  unknown: colors.gray['200'],
};
const DROP_COLOR = colors.gray['250'];
const CHART_FONT = 'inherit';

export const PrioritizationFlowWidget: React.FC<Props> = ({ setShow }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { environmentId, accountId } = useAccessBoundary();
  const { prioritizationFlow } = useSelector(selectVulnerabilities);
  const [chain, setChain] = useState<FlowChainState>(loadFlowChain);
  const filters = activeFilters(chain);
  const filtersKey = filters.join(',');

  useEffect(() => saveFlowChain(chain), [chain]);
  useEffect(() => {
    dispatch(
      actions.getPrioritizationFlow({
        q: {
          environmentId,
          accountId: accountId ? [accountId] : [],
          filters,
          epssThreshold: chain.epssThreshold,
          minSeverity: chain.minSeverity,
        },
      }),
    );
    // filtersKey stands in for the filters array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    environmentId,
    accountId,
    filtersKey,
    chain.epssThreshold,
    chain.minSeverity,
  ]);

  const flow = prioritizationFlow.data;
  const stages = useMemo(() => flow?.stages ?? [], [flow]);
  const measure = chain.measure;
  const measureInfo = FLOW_MEASURES.find(m => m.key === measure)!;
  const measureColor = MEASURE_COLORS[measure];
  const graph = useMemo(() => buildFlowGraph(flow, measure), [flow, measure]);
  const deltas = useMemo(() => stageDeltas(stages, measure), [stages, measure]);
  const first = stages[0];
  const last = stages[stages.length - 1];
  const hasData = !!first && !!graph.links.length;
  const options = {
    epssThreshold: flow?.epss_threshold ?? chain.epssThreshold,
    minSeverity: flow?.min_severity ?? chain.minSeverity,
  };
  // The response describes the chain that was asked for; until a refetch
  // lands, drill links follow the response, not the chips.
  const responseFilters = flow?.filters ?? [];

  const openDetails = (stage: number, severity?: FlowNode['severity']) => {
    const { path } = drillPath(
      measure,
      responseFilters,
      stage,
      options,
      severity,
    );
    setShow('Details');
    navigate(path);
  };
  const finalDrill = drillPath(
    measure,
    responseFilters,
    responseFilters.length,
    options,
  );

  const renderNode = (props: any) => {
    const { x, y, width, height, index, containerWidth } = props;
    const node: FlowNode | undefined = graph.nodes[index];
    if (!node) return <Layer key={`node-${index}`} />;
    const fill =
      node.kind === 'pass'
        ? measureColor
        : node.kind === 'drop'
        ? colors.gray['200']
        : SEVERITY_COLORS[node.severity ?? 'unknown'];
    const labelRight = index === 0 || x + width + 140 < containerWidth;
    const tx = labelRight ? x + width + 6 : x - 6;
    const compact = height < 24;
    const clickable = node.kind !== 'drop';
    return (
      <Layer key={`node-${index}`}>
        <Rectangle
          x={x}
          y={y}
          width={width}
          height={Math.max(height, 2)}
          fill={fill}
          radius={2}
          cursor={clickable ? 'pointer' : 'default'}
          onClick={() =>
            node.kind === 'pass'
              ? openDetails(node.stage ?? 0)
              : node.kind === 'severity'
              ? openDetails(responseFilters.length, node.severity)
              : undefined
          }
        />
        <text
          x={tx}
          y={y + height / 2 + (compact ? 4 : -2)}
          textAnchor={labelRight ? 'start' : 'end'}
          fontSize={12}
          fontFamily={CHART_FONT}
          fontWeight={node.kind === 'pass' ? 600 : 400}
          fill={node.kind === 'drop' ? colors.gray['300'] : colors.black}
          pointerEvents="none"
        >
          {node.name}
          {compact && (
            <tspan fill={colors.gray['300']} fontWeight={400}>
              {' '}
              {formatCount(node.count)}
            </tspan>
          )}
        </text>
        {!compact && (
          <text
            x={tx}
            y={y + height / 2 + 13}
            textAnchor={labelRight ? 'start' : 'end'}
            fontSize={11}
            fontFamily={CHART_FONT}
            fill={colors.gray['300']}
            pointerEvents="none"
          >
            {formatCount(node.count)}
          </text>
        )}
      </Layer>
    );
  };

  const renderLink = (props: any) => {
    const {
      sourceX,
      targetX,
      sourceY,
      targetY,
      sourceControlX,
      targetControlX,
      linkWidth,
      index,
    } = props;
    const link: FlowLink | undefined = graph.links[index];
    if (!link) return <Layer key={`link-${index}`} />;
    const stroke =
      link.kind === 'pass'
        ? measureColor
        : link.kind === 'severity'
        ? SEVERITY_COLORS[link.severity ?? 'unknown']
        : DROP_COLOR;
    const opacity =
      link.kind === 'carry' ? 0.12 : link.kind === 'drop' ? 0.3 : 0.35;
    return (
      <Layer key={`link-${index}`}>
        <path
          d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
          fill="none"
          stroke={stroke}
          strokeOpacity={opacity}
          strokeWidth={Math.max(1, linkWidth)}
        />
      </Layer>
    );
  };

  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const content = describeFlowTooltip(payload[0], graph, measureInfo.unit);
    if (!content) return null;
    const { title, detail } = content;
    return (
      <Box
        bg="white"
        border="1px solid"
        borderColor={colors.gray['100']}
        borderRadius="md"
        px={3}
        py={2}
        boxShadow="md"
        fontSize="sm"
      >
        <Text fontWeight={600}>{title}</Text>
        <Text color={colors.gray['300']} fontSize="xs">
          {detail}
        </Text>
      </Box>
    );
  };

  const measureSwitch = (
    <HStack
      spacing={1}
      bg={colors.gray['50']}
      p={1}
      borderRadius="md"
      role="radiogroup"
      aria-label="Count by"
    >
      {FLOW_MEASURES.map(item => {
        const selected = item.key === measure;
        return (
          <Button
            key={item.key}
            size="xs"
            role="radio"
            aria-checked={selected}
            variant="ghost"
            bg={selected ? 'white' : 'transparent'}
            boxShadow={selected ? 'sm' : undefined}
            fontWeight={selected ? 600 : 500}
            color={selected ? colors.black : colors.gray['300']}
            leftIcon={
              <Box
                boxSize={2}
                borderRadius="sm"
                bg={MEASURE_COLORS[item.key]}
              />
            }
            onClick={() => setChain({ ...chain, measure: item.key })}
          >
            {item.label}
          </Button>
        );
      })}
    </HStack>
  );

  const epssMeta = flow?.epss_score_date
    ? `EPSS scores ${flow.epss_score_date}${
        flow.epss_model_version ? ` (${flow.epss_model_version})` : ''
      }`
    : undefined;

  return (
    <DashboardWidget
      label="Vulnerability Prioritization"
      isLoading={prioritizationFlow.isLoading && !first}
      noData={!prioritizationFlow.isLoading && !hasData}
      noDataMessage={
        prioritizationFlow.isError
          ? 'Vulnerability prioritization is temporarily unavailable'
          : renderNoEntitiesFoundWithIcon('No open vulnerabilities found')
      }
      rightHeader={measureSwitch}
      subHeader={
        <Stack spacing={2} pt={1}>
          {epssMeta && <Text fontSize="xs">{epssMeta}</Text>}
          <FilterChain chain={chain} onChange={setChain} deltas={deltas} />
        </Stack>
      }
      styles={{ contentWrapper: { pb: 0, alignItems: 'stretch' } }}
      content={
        <HStack w="full" h="full" align="stretch" spacing={4}>
          <Box
            flex={1}
            minW={0}
            h="full"
            minH="260px"
            position="relative"
            aria-label={`${measureInfo.label} through the prioritization filters`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <Sankey
                data={graph}
                node={renderNode}
                link={renderLink}
                nodePadding={24}
                nodeWidth={10}
                iterations={0}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <Tooltip content={renderTooltip} />
              </Sankey>
            </ResponsiveContainer>
            {prioritizationFlow.isLoading && (
              <Text
                position="absolute"
                top={0}
                right={0}
                fontSize="xs"
                color={colors.gray['300']}
                bg="white"
                px={2}
                borderRadius="md"
              >
                Updating…
              </Text>
            )}
          </Box>
          <Stack w="200px" flexShrink={0} spacing={3} pt={1}>
            <Stack
              spacing={0}
              px={3}
              py={2.5}
              bg={colors.gray['50']}
              borderRadius="md"
              boxShadow={`inset 3px 0 0 ${measureColor}`}
            >
              <Text fontSize="xs" color={colors.gray['300']}>
                {measureInfo.label} left to act on
              </Text>
              <Text fontSize="3xl" fontWeight={600} lineHeight={1.15}>
                {formatCount(last?.[measure].total ?? 0)}
              </Text>
              <Text fontSize="xs" color={colors.gray['300']}>
                of {formatCount(first?.[measure].total ?? 0)} ·{' '}
                {formatPercent(
                  first?.[measure].total
                    ? (100 * (last?.[measure].total ?? 0)) /
                        first[measure].total
                    : 0,
                )}{' '}
                remain
              </Text>
            </Stack>
            <Stack spacing={1}>
              {FLOW_MEASURES.filter(item => item.key !== measure).map(item => (
                <Button
                  key={item.key}
                  size="sm"
                  variant="outline"
                  borderColor={colors.gray['100']}
                  justifyContent="space-between"
                  fontWeight={500}
                  onClick={() => setChain({ ...chain, measure: item.key })}
                  leftIcon={
                    <Box
                      boxSize={2}
                      borderRadius="sm"
                      bg={MEASURE_COLORS[item.key]}
                    />
                  }
                >
                  <HStack flex={1} justify="space-between" pl={1}>
                    <Text>{item.label}</Text>
                    <Text fontFamily="mono" fontSize="xs">
                      {formatCount(last?.[item.key].total ?? 0)}
                      <Text as="span" color={colors.gray['300']}>
                        {' '}
                        / {formatCount(first?.[item.key].total ?? 0)}
                      </Text>
                    </Text>
                  </HStack>
                </Button>
              ))}
            </Stack>
            <Button
              size="sm"
              variant="link"
              color={colors.primary}
              justifyContent="flex-start"
              onClick={() => {
                setShow('Details');
                navigate(finalDrill.path);
              }}
            >
              View {formatCount(last?.[measure].total ?? 0)} {measureInfo.unit}{' '}
              →
            </Button>
            {finalDrill.omitsEpss && (
              <Text fontSize="xs" color={colors.gray['300']}>
                Details doesn't filter by EPSS yet, so the list there can be
                longer.
              </Text>
            )}
          </Stack>
        </HStack>
      }
    />
  );
};
